import { html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import '../ui/modal-dialog.js';

/**
 * Playback speed control with decrease/increase buttons and tap-to-enter dialog.
 *
 * @fires speed-change - Fired when speed changes (detail: { speed: number })
 */
@customElement('speed-control')
export class SpeedControl extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: inline-flex;
            }

            .container {
                display: flex;
                align-items: center;
                gap: 0.25rem;
                background: #333;
                border-radius: 1rem;
                padding: 0.25rem 0.5rem;
            }

            .speed-btn {
                width: 24px;
                height: 24px;
                border-radius: 50%;
                border: none;
                background: #555;
                color: white;
                font-size: 1rem;
                font-weight: bold;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: background 0.2s;
                line-height: 1;
            }

            .speed-btn:hover:not([disabled]) {
                background: #666;
            }

            .speed-btn[disabled] {
                opacity: 0.5;
                cursor: not-allowed;
            }

            .speed-display {
                font-family: monospace;
                font-size: 0.85rem;
                color: white;
                min-width: 3rem;
                text-align: center;
                cursor: pointer;
                user-select: none;
                -webkit-user-select: none;
                transition: transform 0.1s, background-color 0.2s;
                padding: 0.25rem 0.5rem;
                border-radius: 0.5rem;
            }

            .speed-display:hover {
                background-color: rgba(255, 255, 255, 0.1);
            }

            .speed-display:active {
                transform: scale(0.95);
                background-color: rgba(37, 99, 235, 0.3);
            }

            .dialog-label {
                display: block;
                color: #f1f5f9;
                margin-bottom: 0.5rem;
                font-size: 0.9rem;
            }

            input[type="number"] {
                width: 100%;
                padding: 0.5rem 0.75rem;
                font-size: 1rem;
                background: #0f172a;
                color: #f1f5f9;
                border: 1px solid #475569;
                border-radius: 0.375rem;
                box-sizing: border-box;
            }

            input[type="number"]:focus {
                outline: none;
                border-color: #2563eb;
                box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.3);
            }

            .dialog-error {
                color: #ef4444;
                font-size: 0.85rem;
                margin: 0.75rem 0 0;
            }

            .dialog-btn {
                padding: 0.5rem 1rem;
                border-radius: 0.375rem;
                border: none;
                cursor: pointer;
                font-size: 0.9rem;
            }

            .dialog-btn-primary {
                background: #2563eb;
                color: white;
            }

            .dialog-btn-secondary {
                background: #475569;
                color: white;
            }
        `,
    ];

    @property({ type: Number })
    speed = 1.0;

    @property({ type: Number })
    min = 0.2;

    @property({ type: Number })
    max = 2.0;

    @property({ type: Number })
    step = 0.1;

    @property({ type: Boolean, reflect: true })
    disabled = false;

    @state()
    private _dialogOpen = false;

    @state()
    private _dialogError = '';

    @query('#speed-input')
    private _input?: HTMLInputElement;

    private _decrease(): void {
        if (this.disabled) return;
        const newSpeed = Math.max(this.min, this.speed - this.step);
        this._fireSpeedChange(newSpeed);
    }

    private _increase(): void {
        if (this.disabled) return;
        const newSpeed = Math.min(this.max, this.speed + this.step);
        this._fireSpeedChange(newSpeed);
    }

    private _fireSpeedChange(speed: number): void {
        // Round to avoid floating point artifacts
        speed = Math.round(speed * 100) / 100;
        this.speed = speed;
        this.dispatchEvent(
            new CustomEvent('speed-change', {
                bubbles: true,
                composed: true,
                detail: { speed },
            })
        );
    }

    private _openDialog(): void {
        if (this.disabled) return;
        this._dialogError = '';
        this._dialogOpen = true;
        this.updateComplete.then(() => {
            this._input?.focus();
            this._input?.select();
        });
    }

    private _closeDialog(): void {
        this._dialogOpen = false;
        this._dialogError = '';
    }

    private _submitDialog(): void {
        const raw = this._input?.value.trim() ?? '';
        const cleaned = raw.replace(/%$/, '').trim();
        const n = Number(cleaned);
        if (!Number.isFinite(n) || !Number.isInteger(n)) {
            this._dialogError = 'Enter a whole number between 20 and 200.';
            return;
        }
        if (n < 20 || n > 200) {
            this._dialogError = 'Value must be between 20 and 200.';
            return;
        }
        this._fireSpeedChange(n / 100);
        this._closeDialog();
    }

    private _onInputKeydown(e: KeyboardEvent): void {
        if (e.key === 'Enter') {
            e.preventDefault();
            this._submitDialog();
        }
    }

    render() {
        const speedPercent = Math.round(this.speed * 100);

        return html`
            <div class="container">
                <button
                    class="speed-btn"
                    ?disabled=${this.disabled}
                    @click=${this._decrease}
                >${'\u2212'}</button>
                <span
                    class="speed-display"
                    @click=${this._openDialog}
                >${speedPercent}%</span>
                <button
                    class="speed-btn"
                    ?disabled=${this.disabled}
                    @click=${this._increase}
                >+</button>
            </div>
            <modal-dialog
                .open=${this._dialogOpen}
                heading="Set playback speed"
                size="small"
                @modal-close=${this._closeDialog}
            >
                <label for="speed-input" class="dialog-label">Speed (%)</label>
                <input
                    id="speed-input"
                    type="number"
                    min="20"
                    max="200"
                    step="1"
                    inputmode="numeric"
                    .value=${String(speedPercent)}
                    @keydown=${this._onInputKeydown}
                />
                ${this._dialogError
                    ? html`<p class="dialog-error">${this._dialogError}</p>`
                    : ''}
                <div slot="footer">
                    <button class="dialog-btn dialog-btn-secondary" @click=${this._closeDialog}>Cancel</button>
                    <button class="dialog-btn dialog-btn-primary" @click=${this._submitDialog}>Set</button>
                </div>
            </modal-dialog>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'speed-control': SpeedControl;
    }
}
